import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { lazy, Suspense, useEffect, useRef } from 'react';
import ProtectedRoute from './components/ProtectedRoute';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Loader2 } from 'lucide-react';

// Lazy-loaded pages for PWA performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const TrainerDashboard = lazy(() => import('./pages/TrainerDashboard'));
const TraineeDashboard = lazy(() => import('./pages/TraineeDashboard'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const AccountDeletion = lazy(() => import('./pages/AccountDeletion'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));

// Phase F: Enhanced scroll management
// - New navigation → scroll to top
// - Back navigation → restore previous scroll position
const ScrollManager = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const scrollPositions = useRef({});

  useEffect(() => {
    // Save scroll position before navigation
    const currentPath = pathname;
    const positions = scrollPositions.current;
    
    return () => {
      positions[currentPath] = window.scrollY;
    };
  }, [pathname]);

  useEffect(() => {
    if (navType === 'POP') {
      // Back/forward navigation - restore scroll
      const saved = scrollPositions.current[pathname];
      if (saved !== undefined) {
        requestAnimationFrame(() => window.scrollTo(0, saved));
      }
    } else {
      // New navigation (PUSH/REPLACE) - scroll to top
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
};

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-pt-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 text-pt-primary animate-spin" />
  </div>
);

// Root redirect based on user role
const RootRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on role
  switch (user?.role) {
    case 'trainer':
      return <Navigate to="/trainer" replace />;
    case 'trainee':
      return <Navigate to="/trainee" replace />;
    case 'gym_owner':
      return <Navigate to="/owner" replace />;
    case 'super_admin':
      return <Navigate to="/admin" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollManager />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              
              {/* Legal Pages (Public - No Auth Required) */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/account-deletion" element={<AccountDeletion />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              
              {/* Protected Trainer Routes */}
              <Route 
                path="/trainer" 
                element={
                  <ProtectedRoute allowedRoles={['trainer']}>
                    <TrainerDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Trainee Routes */}
              <Route 
                path="/trainee" 
                element={
                  <ProtectedRoute allowedRoles={['trainee']}>
                    <TraineeDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Owner Routes */}
              <Route 
                path="/owner" 
                element={
                  <ProtectedRoute allowedRoles={['gym_owner']}>
                    <OwnerDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Super Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Root redirect */}
              <Route path="/" element={<RootRedirect />} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#18181B',
              color: '#fff',
              border: '1px solid #27272A',
            },
            className: 'font-body',
          }}
        />
        
        {/* PWA Install Prompt */}
        <PWAInstallPrompt />
      </div>
    </AuthProvider>
  );
}

export default App;
